import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Container } from '@groupelacentrale/contact-storybook';
import { UpdateContactForm, UpdateContactProForm, UpdateVehiclePriceForm } from 'components';
import './UpdateDeposit.scss';
import { FormikProps } from 'formik';
import {
  UpdateVehicleVersionForm,
  UpdateVehicleVersionProps,
} from 'components/VehicleVersionForm/UpdateVehicleVersionForm';
import { UpdateDepositFormBlock } from './UpdateDepositFormBlock';
import { UpdateVehicleDescriptionForm } from 'components/VehicleDescriptionForm/UpdateVehicleDescriptionForm';
import { UpdatePhotoForm } from 'components/Photo/UpdatePhotoForm';
import {
  ContactUpdatedFields,
  WarrantyDesignation,
  UpdateDepositValues,
  VehicleVersionStateFields,
  VehicleType,
} from 'types';
import { useHistory } from 'react-router-dom';

interface Props {
  vehicleType: VehicleType;
  isPro: boolean;
  onReturn: () => void | undefined;
  onSubmit: () => void | undefined;
  errorMessage?: string;
  versionUpdatedFields: Record<keyof UpdateVehicleVersionProps, boolean>;
  contactUpdatedFields: ContactUpdatedFields;
  emails: string[];
  descriptionUpdateFields: number;
  labelos?: WarrantyDesignation[];
  picturesUpdated: number;
}

type FORM_BLOCK = 'version' | 'equipments' | 'photos' | 'contact' | 'price';

export const UpdateDepositFormView: React.FC<Props & FormikProps<UpdateDepositValues>> = ({
  vehicleType,
  isPro,
  onReturn,
  onSubmit,
  isSubmitting,
  errorMessage,
  versionUpdatedFields,
  emails,
  contactUpdatedFields,
  descriptionUpdateFields,
  labelos,
  picturesUpdated,
  ...props
}) => {
  const refsValues: Record<FORM_BLOCK, React.RefObject<HTMLDivElement>> = {
    version: React.createRef<HTMLDivElement>(),
    equipments: React.createRef<HTMLDivElement>(),
    photos: React.createRef<HTMLDivElement>(),
    contact: React.createRef<HTMLDivElement>(),
    price: React.createRef<HTMLDivElement>(),
  };
  const [opened, setOpened] = useState('');
  const history = useHistory();

  useEffect(() => {
    history.replace({ hash: 'prix' });
    setOpened('prix');
  }, [history]);

  const handleOpen = useCallback(
    (id?: string) => {
      setOpened(id || '');
      history.replace({ hash: id });
    },
    [history],
  );

  const handleClose = useCallback(() => {
    setOpened('');
    history.replace({ hash: undefined });
  }, [history]);

  const handleSubmit = () => {
    if (Object.keys(props.errors).length > 0) {
      let blockToOpen: FORM_BLOCK | undefined;
      if (props.errors.hasOwnProperty('vehiclePrice')) {
        blockToOpen = 'price';
      }
      for (const key of Object.keys(contactUpdatedFields)) {
        if (props.errors.hasOwnProperty(key)) {
          blockToOpen = 'contact';
        }
      }
      for (const key of Object.keys(versionUpdatedFields)) {
        if (props.errors.hasOwnProperty(key)) {
          blockToOpen = 'version';
        }
      }

      if (blockToOpen) {
        handleOpen(blockToOpen);
        //eslint-disable-next-line @typescript-eslint/no-unused-expressions
        refsValues[blockToOpen!].current?.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      onSubmit();
    }
  };

  return (
    <div className="update-deposit-form">
      <UpdateDepositFormBlock
        ref={refsValues.version}
        title="Votre véhicule"
        open={opened === 'version'}
        id="version"
        onOpen={handleOpen}
        onClose={handleClose}
        updatedFields={Object.keys(versionUpdatedFields).reduce(
          (count, currentKey) => count + (versionUpdatedFields[currentKey as keyof VehicleVersionStateFields] ? 1 : 0),
          0,
        )}
        errorFields={Object.keys(versionUpdatedFields).reduce(
          (count, currentKey) =>
            count + (props.errors.hasOwnProperty(currentKey as keyof VehicleVersionStateFields) ? 1 : 0),
          0,
        )}
      >
        <UpdateVehicleVersionForm
          vehicleType={vehicleType}
          updatedFields={versionUpdatedFields}
          isSubmitting={isSubmitting}
          {...props}
        />
      </UpdateDepositFormBlock>
      <UpdateDepositFormBlock
        ref={refsValues.equipments}
        title="Vos équipements"
        open={opened === 'equipements'}
        id="equipements"
        onOpen={handleOpen}
        onClose={handleClose}
        updatedFields={descriptionUpdateFields}
      >
        <UpdateVehicleDescriptionForm isPro={isPro} labelos={labelos} isSubmitting={isSubmitting} {...props} />
      </UpdateDepositFormBlock>
      <UpdateDepositFormBlock
        ref={refsValues.photos}
        title="Photos de votre véhicule"
        open={opened === 'photos'}
        id="photos"
        onOpen={handleOpen}
        onClose={handleClose}
        updatedFields={picturesUpdated}
      >
        <UpdatePhotoForm isSubmitting={isSubmitting} {...props} />
      </UpdateDepositFormBlock>
      <UpdateDepositFormBlock
        ref={refsValues.contact}
        title="Vos coordonnées de contact"
        open={opened === 'contact'}
        id="contact"
        onOpen={handleOpen}
        onClose={handleClose}
        updatedFields={Object.keys(contactUpdatedFields).reduce(
          (count, currentKey) => count + (contactUpdatedFields[currentKey as keyof ContactUpdatedFields] ? 1 : 0),
          0,
        )}
        errorFields={Object.keys(contactUpdatedFields).reduce(
          (count, currentKey) =>
            count + (props.errors.hasOwnProperty(currentKey as keyof ContactUpdatedFields) ? 1 : 0),
          0,
        )}
      >
        {isPro ? (
          <UpdateContactProForm
            emails={emails}
            updatedFields={contactUpdatedFields}
            {...props}
            isSubmitting={isSubmitting}
          />
        ) : (
          <UpdateContactForm updatedFields={contactUpdatedFields} {...props} isSubmitting={isSubmitting} />
        )}
      </UpdateDepositFormBlock>

      <UpdateDepositFormBlock
        ref={refsValues.price}
        title="Prix de votre véhicule"
        open={opened === 'prix'}
        id="prix"
        onOpen={handleOpen}
        onClose={handleClose}
        updatedFields={Number(props.values.vehiclePrice) !== props.initialValues.vehiclePrice ? 1 : 0}
        errorFields={props.errors.hasOwnProperty('vehiclePrice') ? 1 : 0}
      >
        <UpdateVehiclePriceForm isSubmitting={isSubmitting} {...props} />
      </UpdateDepositFormBlock>

      <Container shadowed paddings="mediumP" backgroundColor="bgWhite" className="actions">
        <span className="btns">
          <Button label="Annuler" theme="quaternary" type="button" onClick={onReturn} />
          <Button
            label="Valider les modifications"
            theme="primary"
            type="button"
            loading={isSubmitting}
            onClick={handleSubmit}
          />
        </span>

        {contactUpdatedFields.phone && !isPro && (
          <span className="info">
            Vous devrez confirmer votre nouveau numéro de téléphone pour valider les modifications.
          </span>
        )}
      </Container>

      {errorMessage && <Alert type="error">{errorMessage}</Alert>}
    </div>
  );
};
